import React, { useEffect, useMemo, useState } from "react";

import { adminReservationsService } from "api/services/admin-reservations.service";
import { instructorSchedulesService } from "api/services/instructor-schedules.service";
import { practiceReservationsService } from "api/services/practice-reservations.service";
import { practiceSchedulesService } from "api/services/practice-schedules.service";
import { usersService } from "api/services/users.service";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import { memberMutationId } from "utils/admin-user-list";
import { getApiErrorMessage } from "utils/api-error";
import { boundMemberSearchInput, decideMemberSearch, MEMBER_SEARCH_LABEL, MEMBER_SEARCH_LIMIT, MEMBER_SEARCH_INVALID_NOTICE, MEMBER_SEARCH_TOO_LONG_NOTICE, MEMBER_SEARCH_PHONE_NOTICE, MEMBER_SEARCH_TOO_SHORT_NOTICE } from "utils/member-search";

import type { ReservationUser } from "api/types/instructor-schedules.types";
import type { PracticeReservationUser } from "api/types/practice-schedules.types";
import type { AdminUserListItem } from "api/types/user.types";

import "./ReservationListModal.scss";

export type ReservationScheduleType = "session" | "practice";

// 부모 리스트에 낙관적 업데이트를 알리기 위한 변경 정보
export interface ReservationCountChange {
  scheduleId: number;
  confirmedDelta: number;
  waitingDelta: number;
}

// 두 도메인 공통으로 다루는 예약자 표시 데이터
interface ModalReservationUser {
  id: number;
  user_id: string | null;
  name: string;
  hp: string;
  status: string;
  is_waiting: boolean;
  queue_position: number | null;
  created_at: string;
}

interface ReservationListModalProps {
  open: boolean;
  scheduleId: number | null;
  scheduleType: ReservationScheduleType;
  title: string;
  onClose: () => void;
  onChanged?: (change: ReservationCountChange) => void;
}

const RESERVATION_STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "info" | "draft"> = {
  RESERVED: "info",
  ATTENDED: "active",
  ABSENT: "rejected",
  NEEDS_RETAKE: "pending",
  CANCELLED: "draft",
  COMPLETED: "active",
};

const toModalUser = (u: ReservationUser | PracticeReservationUser): ModalReservationUser => ({
  id: u.id,
  user_id: u.user_id,
  name: u.name,
  hp: u.hp,
  status: String(u.status),
  is_waiting: u.is_waiting,
  queue_position: u.queue_position,
  created_at: u.created_at,
});

const ReservationListModal: React.FC<ReservationListModalProps> = ({ open, scheduleId, scheduleType, title, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reservations, setReservations] = useState<{
    confirmed: ModalReservationUser[];
    waiting: ModalReservationUser[];
  }>({ confirmed: [], waiting: [] });

  // 강제 예약 폼
  const [showForceForm, setShowForceForm] = useState(false);
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [userSearchNotice, setUserSearchNotice] = useState("");
  const [selectedUser, setSelectedUser] = useState<AdminUserListItem | null>(null);
  const [skipValidation, setSkipValidation] = useState(false);
  const [forceSubmitting, setForceSubmitting] = useState(false);
  const [forceError, setForceError] = useState("");
  // 회원 검색 전용 오류 — 제출 오류(`forceError`)와 분리한다.
  const [userSearchError, setUserSearchError] = useState("");

  // 취소 진행 상태 (행 단위)
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const fetchDetail = async () => {
    if (!scheduleId) return;
    setLoading(true);
    setError("");
    try {
      if (scheduleType === "practice") {
        const res = await practiceSchedulesService.get(scheduleId);
        setReservations({
          confirmed: (res.data.reservations?.confirmed ?? []).map(toModalUser),
          waiting: (res.data.reservations?.waiting ?? []).map(toModalUser),
        });
      } else {
        const res = await instructorSchedulesService.get(scheduleId);
        setReservations({
          confirmed: (res.data.reservations?.confirmed ?? []).map(toModalUser),
          waiting: (res.data.reservations?.waiting ?? []).map(toModalUser),
        });
      }
    } catch (err) {
      setReservations({ confirmed: [], waiting: [] });
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !scheduleId) return;
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleId, scheduleType]);

  // 모달이 닫히거나 scheduleId 가 바뀌면 폼 상태 초기화
  useEffect(() => {
    if (!open) {
      setShowForceForm(false);
      setUserQuery("");
      setSelectedUser(null);
      setSkipValidation(false);
      setForceError("");
    }
  }, [open, scheduleId]);

  // 회원 검색은 BE 에 위임한다 (디바운스 300ms).
  // 폼을 열 때 전 회원을 받아두던 방식은 `page_size` 상한(200) 때문에 성립하지
  // 않는다 — 첫 페이지만 받아놓고 클라이언트에서 거르면 그 밖의 회원이 조용히
  // 검색되지 않는다.
  useEffect(() => {
    if (!open || !showForceForm) return;

    const decision = decideMemberSearch(userQuery);
    setUserSearchNotice(decision.kind === "phone-blocked" ? MEMBER_SEARCH_PHONE_NOTICE : decision.kind === "invalid-empty" ? MEMBER_SEARCH_INVALID_NOTICE : decision.kind === "too-long" ? MEMBER_SEARCH_TOO_LONG_NOTICE : decision.kind === "too-short" ? MEMBER_SEARCH_TOO_SHORT_NOTICE : "");

    // 전화번호 형태·너무 짧은 입력은 **요청 자체를 만들지 않는다** —
    // 검색어는 GET 쿼리로 나가 로그·히스토리에 남는다.
    if (decision.kind !== "search") {
      setUsers([]);
      setUsersLoading(false);
      setUserSearchError("");
      return;
    }

    let cancelled = false;
    setUsersLoading(true);
    // 새 검색이 시작되면 이전 검색 오류를 즉시 해제한다.
    setUserSearchError("");
    const timer = setTimeout(async () => {
      try {
        const res = await usersService.search(decision.term, { page_size: MEMBER_SEARCH_LIMIT });
        if (cancelled) return;
        setUsers(res.data.results);
        setUserSearchError("");
      } catch (err) {
        if (cancelled) return;
        setUsers([]);
        // 검색 실패는 제출 오류(`forceError`)와 **다른 상태**다 — 같은 상태를 쓰면
        // 서로를 덮고, 후보 영역은 "일치하는 회원이 없습니다" 로 장애를 가린다.
        setUserSearchError(getApiErrorMessage(err) || "회원 검색에 실패했습니다. 잠시 후 다시 시도하세요.");
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, showForceForm, userQuery]);

  // 후보는 서버가 이미 필터·상한 처리한 결과다. 방어적으로 상한만 한 번 더 건다.
  const candidates = useMemo(() => users.slice(0, MEMBER_SEARCH_LIMIT), [users]);

  const handleForceSubmit = async () => {
    if (!scheduleId) return;
    if (!selectedUser) {
      setForceError("회원을 선택하세요.");
      return;
    }
    // 구 배열 응답에는 `public_id` 가 없을 수 있다. 그대로 보내면 **빈 문자열을 POST** 한다.
    // BE `resolve_user_by_identifier` 는 `user_id` 도 받으므로 그것으로 떨어지고,
    // 둘 다 없으면 요청 자체를 만들지 않는다 — 빈 식별자로 서버를 두드리지 않는다.
    const identifier = memberMutationId(selectedUser);
    if (!identifier) {
      setForceError("이 회원은 식별자가 없어 예약할 수 없습니다.");
      return;
    }
    setForceSubmitting(true);
    setForceError("");

    // 낙관적 업데이트: 강제 예약은 항상 확정 +1로 가정
    const optimistic: ReservationCountChange = {
      scheduleId,
      confirmedDelta: 1,
      waitingDelta: 0,
    };
    onChanged?.(optimistic);

    // ADR-021 — 대상 회원 식별자는 public_id(UUID) 로 전송한다.
    // 백엔드 dual-lookup 미배포 상태에서는 USER_NOT_FOUND(404) 가 오므로
    // 로그인 user_id 로 1회 폴백 재시도한다.
    const basePayload = {
      schedule_id: scheduleId,
      schedule_type: scheduleType,
      skip_validation: skipValidation,
    };
    try {
      try {
        await adminReservationsService.forceReservation({
          ...basePayload,
          user_id: identifier,
        });
      } catch (primaryErr) {
        const status = (primaryErr as { response?: { status?: number } })?.response?.status;
        const code = (primaryErr as { response?: { data?: { code?: string } } })?.response?.data?.code;
        // 404 USER_NOT_FOUND 인 경우에만 로그인 user_id 로 폴백.
        // 그 외 에러(중복 예약·시간 충돌 등)는 재시도하지 않고 그대로 던진다.
        // 404 USER_NOT_FOUND 면 로그인 `user_id` 로 폴백한다. `identifier` 가 이미
        // `user_id` 였다면 같은 요청이 되므로 그때는 재시도하지 않는다.
        if (status === 404 && code === "USER_NOT_FOUND" && identifier !== selectedUser.user_id && selectedUser.user_id) {
          await adminReservationsService.forceReservation({
            ...basePayload,
            user_id: selectedUser.user_id,
          });
        } else {
          throw primaryErr;
        }
      }
      setShowForceForm(false);
      setSelectedUser(null);
      setUserQuery("");
      setSkipValidation(false);
      await fetchDetail();
    } catch (err) {
      // 실패 시 롤백
      onChanged?.({
        scheduleId,
        confirmedDelta: -optimistic.confirmedDelta,
        waitingDelta: -optimistic.waitingDelta,
      });
      setForceError(getApiErrorMessage(err));
    } finally {
      setForceSubmitting(false);
    }
  };

  const handleAdminCancel = async (reservation: ModalReservationUser) => {
    if (!scheduleId) return;
    const ok = window.confirm(`${reservation.name}(${reservation.user_id ?? "-"}) 님의 예약을 취소하시겠습니까?`);
    if (!ok) return;
    const reason = window.prompt("취소 사유를 입력하세요. (선택)", "")?.trim() ?? "";

    // 낙관적 업데이트: 대기 예약이면 waiting -1, 확정 예약이면 confirmed -1
    const optimistic: ReservationCountChange = {
      scheduleId,
      confirmedDelta: reservation.is_waiting ? 0 : -1,
      waitingDelta: reservation.is_waiting ? -1 : 0,
    };
    onChanged?.(optimistic);

    setCancellingId(reservation.id);
    try {
      const payload = reason ? { reason } : undefined;
      if (scheduleType === "practice") {
        await practiceReservationsService.adminCancel(reservation.id, payload);
      } else {
        await adminReservationsService.adminCancel(reservation.id, payload);
      }
      await fetchDetail();
    } catch (err) {
      // 실패 시 롤백
      onChanged?.({
        scheduleId,
        confirmedDelta: -optimistic.confirmedDelta,
        waitingDelta: -optimistic.waitingDelta,
      });
      alert(getApiErrorMessage(err));
    } finally {
      setCancellingId(null);
    }
  };

  const columns: Column<ModalReservationUser>[] = [
    { key: "name", title: "이름" },
    { key: "user_id", title: "아이디" },
    { key: "hp", title: "연락처" },
    {
      key: "status",
      title: "상태",
      render: (v) => <Badge variant={RESERVATION_STATUS_BADGE[String(v)] ?? "draft"}>{String(v)}</Badge>,
    },
    {
      key: "is_waiting",
      title: "대기",
      render: (v, row) => (v ? <Badge variant="pending">대기 #{row.queue_position}</Badge> : "-"),
    },
    {
      key: "created_at",
      title: "신청일",
      render: (v) => String(v).slice(0, 10),
    },
    {
      key: "actions",
      title: "관리",
      width: "110px",
      render: (_, row) => {
        const isCancelled = row.status === "CANCELLED";
        return (
          <div className="reservation-row-actions">
            <Button size="sm" variant="danger" onClick={() => handleAdminCancel(row)} disabled={isCancelled || cancellingId === row.id}>
              {cancellingId === row.id ? "취소 중..." : "예약 취소"}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <Modal open={open} title={title || "예약자 목록"} width="900px" onClose={onClose} closeOnOverlayClick={false}>
      {loading ? (
        <div className="admin-page__loading">불러오는 중...</div>
      ) : error ? (
        <div className="admin-page__error">{error}</div>
      ) : (
        <>
          <div className="reservation-modal__toolbar">
            <Button
              variant={showForceForm ? "ghost" : "primary"}
              size="sm"
              onClick={() => {
                setShowForceForm((v) => !v);
                setForceError("");
              }}
            >
              {showForceForm ? "닫기" : "추가"}
            </Button>
          </div>

          {showForceForm && (
            <div className="force-reservation-form">
              <div className="force-reservation-form__title">강제 예약 추가 ({scheduleType === "practice" ? "연습반" : "강습"})</div>

              <div className="force-reservation-form__row">
                <Input
                  label={MEMBER_SEARCH_LABEL}
                  placeholder="검색어를 입력하세요"
                  value={userQuery}
                  onChange={(e) => {
                    // HTML `maxLength` 대신 판정 쪽에서 자른다 — 속성은 astral 문자를 반으로 쪼갠다.
                    setUserQuery(boundMemberSearchInput(e.target.value));
                    setSelectedUser(null);
                  }}
                  prefix="🔍"
                />
              </div>

              {/* idle 부터 **항상 있는** live region — 노드가 생겼다 없어지면 알림이 안 간다. */}
              <div data-sr-live role="status" aria-live="polite" aria-atomic="true" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
                {userSearchNotice}
              </div>
              {userSearchNotice ? (
                <div className="force-reservation-form__empty">{userSearchNotice}</div>
              ) : userSearchError ? (
                <div className="force-reservation-form__empty" style={{ color: "#EF4444" }} role="alert">
                  회원 검색에 실패했습니다 — {userSearchError}
                </div>
              ) : usersLoading ? (
                <div className="force-reservation-form__empty">회원 목록 불러오는 중...</div>
              ) : userQuery.trim() === "" ? (
                <div className="force-reservation-form__empty">검색어를 입력하면 후보 회원이 표시됩니다.</div>
              ) : candidates.length === 0 ? (
                <div className="force-reservation-form__empty">일치하는 회원이 없습니다.</div>
              ) : (
                <div className="force-reservation-form__candidates">
                  {candidates.map((u) => (
                    <div key={u.id} className={`force-reservation-form__candidate${selectedUser?.id === u.id ? " force-reservation-form__candidate--selected" : ""}`} onClick={() => setSelectedUser(u)}>
                      <span>
                        {u.name} <span style={{ color: "#71717a" }}>@{u.user_id}</span>
                      </span>
                      {u.hp_masked && <span style={{ color: "#71717a", fontFamily: "monospace" }}>{u.hp_masked}</span>}
                    </div>
                  ))}
                </div>
              )}

              {selectedUser && (
                <div className="force-reservation-form__selected">
                  선택된 회원: <strong>{selectedUser.name}</strong> (@{selectedUser.user_id})
                </div>
              )}

              <label className="force-reservation-form__options">
                <input type="checkbox" checked={skipValidation} onChange={(e) => setSkipValidation(e.target.checked)} />
                정원/자격 검증 건너뛰기 (skip_validation)
              </label>

              {forceError && <div className="reservation-modal__error">{forceError}</div>}

              <div className="force-reservation-form__actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowForceForm(false);
                    setSelectedUser(null);
                    setUserQuery("");
                    setSkipValidation(false);
                    setForceError("");
                  }}
                  disabled={forceSubmitting}
                >
                  취소
                </Button>
                <Button variant="primary" size="sm" onClick={handleForceSubmit} disabled={forceSubmitting || !selectedUser}>
                  {forceSubmitting ? "추가 중..." : "예약 추가"}
                </Button>
              </div>
            </div>
          )}

          <div className="reservation-section">
            <h4 className="reservation-section__title">확정 ({reservations.confirmed.length}명)</h4>
            <Table columns={columns} data={reservations.confirmed} rowKey="id" />
          </div>
          <div className="reservation-section">
            <h4 className="reservation-section__title">대기 ({reservations.waiting.length}명)</h4>
            <Table columns={columns} data={reservations.waiting} rowKey="id" />
          </div>
        </>
      )}
    </Modal>
  );
};

export default ReservationListModal;
